import { SingleUnitGetTargetQuery$data } from '../graphQLOperations/__generated__/SingleUnitGetTargetQuery.graphql';
import { SkillsByType } from '../models/SingleUnit';
import { TargetSkillTypeLabel } from '../models/TargetSkillType';
import { DropdownItemModel } from '../pages/SingleUnit';
import { TargetSkillNode } from './getHighAndMediumRelevancesAncestries';
import { sortByKey } from './sortCriteria';

type UnitSkillsData = TargetSkillNode | SingleUnitGetTargetQuery$data;

/**
 * The optional multiselect filters default to every option selected, which the UI shows as "All".
 * That state means "no filter", so it must collapse to undefined before reaching the filtering
 * helpers: passing the full id list instead would drop skills that are not associated with any
 * option (see filterOrAddSkillsByLessons, which filters targetSkills whenever a selection exists).
 */
export const isAllSelected = (selectedIds: string[], options: DropdownItemModel[]): boolean =>
  options.length > 0 && selectedIds.length >= options.length;

export const asNeutralSelection = (selectedIds: string[], options: DropdownItemModel[]): string[] | undefined =>
  selectedIds.length === 0 || isAllSelected(selectedIds, options) ? undefined : selectedIds;

/** Standards attached to the skills of the unit, de-duplicated and sorted by their short name. */
export const getStandardOptionsFromUnit = (data: UnitSkillsData | null | undefined): DropdownItemModel[] => {
  const standards = new Map<string, string>();

  data?.target?.targetSkills?.forEach(targetSkill => {
    targetSkill?.skill?.standards?.forEach(standard => {
      if (standard?.id && standard.shortName) standards.set(standard.id, standard.shortName);
    });
  });

  return Array.from(standards, ([value, label]) => ({ value, label })).sort(sortByKey<DropdownItemModel>('label'));
};

/**
 * A skill survives the Standards filter when it carries any of the selected standards. Skills with no
 * standards at all therefore drop out while a filter is active, which `some` gives us for free on an
 * empty list.
 */
export const skillMatchesStandards = (skillStandardIds: string[], selectedStandardIds: string[]): boolean =>
  skillStandardIds.some(id => selectedStandardIds.includes(id));

/**
 * The optional filters only narrow target skills: Additional Support and Extension keep listing the
 * whole unit, and the left-column counts stay on the unfiltered figures. Takes the target group from
 * the narrowed set and every other group from the untouched one.
 */
export const withFilteredTargetSkills = (unfiltered: SkillsByType[], narrowed: SkillsByType[]): SkillsByType[] =>
  unfiltered.map(group =>
    group.targetSkillType === TargetSkillTypeLabel.TARGET
      ? narrowed.find(narrowedGroup => narrowedGroup.targetSkillType === TargetSkillTypeLabel.TARGET) ?? group
      : group,
  );

/**
 * Narrows the unit to the skills matching the selected standards. Mirrors filterOrAddSkillsByLessons:
 * an undefined selection is the neutral state and passes the data straight through.
 */
export const filterSkillsByStandards = (data: UnitSkillsData, selectedStandardIds: string[] | undefined) => {
  if (!selectedStandardIds) return data;

  const targetSkills = data?.target?.targetSkills?.filter(targetSkill =>
    skillMatchesStandards(
      (targetSkill?.skill?.standards ?? []).flatMap(standard => (standard?.id ? [standard.id] : [])),
      selectedStandardIds,
    ),
  );

  return { ...data, target: { ...data.target, targetSkills } };
};
